import React, { Component, Fragment } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Bread, Crumb } from './common/Bread'
import Divider from './common/Divider'
import Block from './common/Block'
import { Anchor } from './common/Anchor'
import { Tabs, TabPane, Spin, Icon, Popover } from './common/Ant'
import { toTitleCase } from '../utils/textFormatters'
import { dateTimeFormatter } from '../utils/date'
import {
  USER_PERMISSIONS,
  ROLES,
  RESOURCE_TYPES,
  PRESSURE_MONITOR_MEASUREMENT_UNITS,
} from '../constants'
import { hasPermission } from '../utils/hasPermission'
import { getPathSnippets } from '../utils/helpers'
import EditHistory from './EditHistory'
import AllComments from './AllComments'
import AssignmentHistory from './AssignmentHistory'
import PressureMonitorData from './PressureMonitorData'

const labelWithInfo = (text, info) => (
  <span>
    {text}{' '}
    <Popover
      content={<div style={{ maxWidth: 260 }}>{info}</div>}
      trigger={['hover', 'click']}
    >
      <Icon type="question-circle-o" className="pointer" />
    </Popover>
  </span>
)

class PressureMonitor extends Component {
  static propTypes = {
    pressureMonitor: PropTypes.object.isRequired,
    getPressureMonitor: PropTypes.func.isRequired,
    isPressureMonitorLoading: PropTypes.bool,
    userRole: PropTypes.string,
    match: PropTypes.object,
    updateQueryParams: PropTypes.func.isRequired,
    isHistoryLoading: PropTypes.bool,
    editHistory: PropTypes.arrayOf(PropTypes.object),
    getEditHistory: PropTypes.func.isRequired,
    assignmentHistory: PropTypes.arrayOf(PropTypes.object),
    getAssignmentHistory: PropTypes.func.isRequired,
    isCommentLoading: PropTypes.bool,
    getAllComments: PropTypes.func.isRequired,
    deleteComment: PropTypes.func.isRequired,
    createComment: PropTypes.func.isRequired,
    editComment: PropTypes.func.isRequired,
    getAllTags: PropTypes.func.isRequired,
    tags: PropTypes.arrayOf(PropTypes.object),
    comments: PropTypes.arrayOf(PropTypes.object),
    pressureMonitorData: PropTypes.object.isRequired,
    getPressureMonitorData: PropTypes.func.isRequired,
    isChartDataLoading: PropTypes.bool.isRequired,
  }

  getActiveTabFromPath = () => {
    const snippets = getPathSnippets(global.location.pathname)
    const lastSnippet = snippets[snippets.length - 1]
    const { id } = this.props.match.params

    return lastSnippet === id ? 'details' : lastSnippet
  }

  componentDidMount() {
    this.props.getPressureMonitor(this.props.match.params.id)
    this.handleTabSelection(this.getActiveTabFromPath())
  }

  handleTabSelection = entity => {
    const { updateQueryParams, match } = this.props
    const activeTab = this.getActiveTabFromPath()

    updateQueryParams({
      pathname: `${match.url}/${entity}`,
      search: !activeTab || entity === activeTab ? global.location.search : '',
    })
  }

  render() {
    const {
      pressureMonitor,
      isPressureMonitorLoading,
      userRole,
      match,
      isHistoryLoading,
      editHistory,
      getEditHistory,
      assignmentHistory,
      getAssignmentHistory,
      isCommentLoading,
      getAllComments,
      deleteComment,
      createComment,
      editComment,
      getAllTags,
      tags,
      comments,
      pressureMonitorData,
      getPressureMonitorData,
      isChartDataLoading,
      updateQueryParams,
    } = this.props
    const canViewAdminFields =
      [ROLES.PILLAR_ADMIN, ROLES.SUPER_ADMIN].indexOf(userRole) !== -1

    const {
      id,
      pillarId,
      moduleId,
      status,
      description,
      activateThreshold,
      deactivateThreshold,
      audioAlert,
      toggleSign,
      reportIntervalS,
      violationDelay,
      cooldownDelayS,
      audibleDelay,
      cancelDelay,
      displayTimeoutS,
      measurementUnits,
      online,
      lastSeen,
      assetLocationPillarId,
      siteSlug,
      siteName,
      locationId,
      locationName,
      floorId,
      floorName,
      buildingId,
      buildingName,
    } = pressureMonitor
    const measurementUnitLabel =
      PRESSURE_MONITOR_MEASUREMENT_UNITS[measurementUnits]

    return isPressureMonitorLoading ? (
      <Spin size="large" className="w-100 center mv5" />
    ) : (
      <section className="PressureMonitor mb3">
        <div className="mb3">
          <div className="mb2">
            <Bread>
              <Crumb>
                <Anchor to="/inventory/all">Inventory</Anchor>
              </Crumb>
              <Crumb>
                <Anchor to={`/inventory/all/pressure-monitors`}>
                  Pressure Monitors
                </Anchor>
              </Crumb>
              <Crumb>{pillarId}</Crumb>
            </Bread>
          </div>
          {hasPermission(USER_PERMISSIONS.EDIT_PRESSURE_MONITOR) && (
            <div className="flex justify-center mb3">
              <Anchor to={`${match.url}/edit`}>Edit</Anchor>
            </div>
          )}
          <Divider />
        </div>
        {pillarId && (
          <div className="mb3">
            <PressureMonitorData
              pillarId={pillarId}
              pressureMonitorData={pressureMonitorData}
              getPressureMonitorData={getPressureMonitorData}
              isLoading={isChartDataLoading}
              updateQueryParams={updateQueryParams}
            />
            <Divider />
          </div>
        )}
        <Switch>
          <Route
            path={`${match.url}/`}
            exact
            render={props => <Redirect to={`${match.url}/details`} />}
          />
          <Fragment>
            <Tabs
              defaultActiveKey="details"
              type="card"
              activeKey={this.getActiveTabFromPath()}
              onTabClick={this.handleTabSelection}
            >
              <TabPane tab="Details" key="details">
                <Route
                  path={`${match.url}/details`}
                  exact
                  render={props => (
                    <div className="flex flex-wrap">
                      <Block
                        label={labelWithInfo(
                          'Pillar ID',
                          'Must be 3 letters followed by 2 numbers (e.g. PAA00). Leave blank to auto-generate the next available ID.'
                        )}
                      >
                        {pillarId}
                      </Block>
                      <Block
                        label={labelWithInfo(
                          'Module ID',
                          'The 16 character serial number on the Pressure Monitor.'
                        )}
                      >
                        {moduleId}
                      </Block>
                      <Block
                        label={labelWithInfo(
                          'Status',
                          'Current status of this pressure monitor (e.g. inventory, deployed, spare).'
                        )}
                      >
                        {toTitleCase(status)}
                      </Block>
                      <Block
                        label={labelWithInfo(
                          'Online',
                          'Whether the device has reported in recently. Updates automatically based on device activity.'
                        )}
                      >
                        {online ? (
                          <span className="green">
                            <Icon type="check-circle-o" className="pr1" />
                            Online
                          </span>
                        ) : (
                          <span className="red">
                            <Icon type="poweroff" className="pr1" />
                            Offline
                          </span>
                        )}
                      </Block>
                      <Block
                        label={labelWithInfo(
                          'Last Seen',
                          'The last time this device reported in.'
                        )}
                      >
                        {lastSeen ? dateTimeFormatter(lastSeen, true) : '--'}
                      </Block>
                      <Block
                        label={labelWithInfo(
                          'Description',
                          'Free-form notes about this pressure monitor.'
                        )}
                      >
                        {description}
                      </Block>
                      <Block
                        label={labelWithInfo(
                          `Activate Threshold${
                            measurementUnitLabel
                              ? ` (${measurementUnitLabel})`
                              : ''
                          }`,
                          'Activates alerts and alarm when pressure goes below this threshold.'
                        )}
                      >
                        {activateThreshold}
                      </Block>
                      <Block
                        label={labelWithInfo(
                          `Deactivate Threshold${
                            measurementUnitLabel
                              ? ` (${measurementUnitLabel})`
                              : ''
                          }`,
                          'Deactivates alarm when pressure goes above this threshold.'
                        )}
                      >
                        {deactivateThreshold}
                      </Block>
                      <Block
                        label={labelWithInfo(
                          'Audio Alert',
                          'When on, the device plays an audible alert when the activate threshold is crossed.'
                        )}
                      >
                        {audioAlert ? 'Yes' : 'No'}
                      </Block>
                      {canViewAdminFields && (
                        <>
                          <Block
                            label={labelWithInfo(
                              'Toggle Sign',
                              "Inverts the sign of the pressure value shown on the device's display. Admin only."
                            )}
                          >
                            {toggleSign ? 'Yes' : 'No'}
                          </Block>
                          <Block
                            label={labelWithInfo(
                              'Violation Delay (sec)',
                              'Window size, in seconds, used to detect a violation of average readings. Admin only.'
                            )}
                          >
                            {violationDelay}
                          </Block>
                          <Block
                            label={labelWithInfo(
                              'Cooldown Delay (sec)',
                              'Window size, in seconds, used to detect a restoration of average readings. Admin only.'
                            )}
                          >
                            {cooldownDelayS}
                          </Block>
                          <Block
                            label={labelWithInfo(
                              'Audible Delay (sec)',
                              'Delay, in seconds, before the buzzer is activated. Admin only.'
                            )}
                          >
                            {audibleDelay}
                          </Block>
                          <Block
                            label={labelWithInfo(
                              'Cancel Delay (sec)',
                              'When the button is pressed, the delay before a violation alert can be generated again. Admin only.'
                            )}
                          >
                            {cancelDelay}
                          </Block>
                          <Block
                            label={labelWithInfo(
                              'Display Timeout (sec)',
                              'Timeout for display. When on battery, both the LCD and backlight will be powered off when the timer expires. Admin only.'
                            )}
                          >
                            {displayTimeoutS}
                          </Block>
                        </>
                      )}
                      <Block
                        label={labelWithInfo(
                          'Measurement Units',
                          'Unit of measurement pressure readings are reported and displayed in.'
                        )}
                      >
                        {PRESSURE_MONITOR_MEASUREMENT_UNITS[measurementUnits]}
                      </Block>
                      {canViewAdminFields && (
                        <Block
                          label={labelWithInfo(
                            'Report Interval (sec)',
                            'How often, in seconds, the device reports readings over the network. Admin only.'
                          )}
                        >
                          {reportIntervalS}
                        </Block>
                      )}
                      <Block
                        label={labelWithInfo(
                          'Asset Location',
                          'The asset location this pressure monitor is stored at when not deployed to a site.'
                        )}
                      >
                        {assetLocationPillarId && (
                          <Anchor
                            to={`/inventory/asset-locations/${assetLocationPillarId}`}
                          >
                            {assetLocationPillarId}
                          </Anchor>
                        )}
                      </Block>
                      <Block label="Site">
                        {siteSlug && (
                          <Anchor to={`/sites/${siteSlug}`}>{siteName}</Anchor>
                        )}
                      </Block>
                      <Block label="Building">
                        {siteSlug && buildingId && (
                          <Anchor to={`/sites/${siteSlug}/buildings/${buildingId}`}>
                            {buildingName}
                          </Anchor>
                        )}
                      </Block>
                      <Block label="Floor">
                        {siteSlug && floorId && (
                          <Anchor to={`/sites/${siteSlug}/floors/${floorId}`}>
                            {floorName}
                          </Anchor>
                        )}
                      </Block>
                      <Block label="Location">
                        {siteSlug && floorId && locationId && (
                          <Anchor
                            to={`/sites/${siteSlug}/floors/${floorId}/locations/${locationId}`}
                          >
                            {locationName}
                          </Anchor>
                        )}
                      </Block>
                    </div>
                  )}
                />
              </TabPane>
              {hasPermission(USER_PERMISSIONS.VIEW_COMMENT) && (
                <TabPane tab="Comments" key="comments">
                  <Route
                    path={`${match.url}/comments`}
                    exact
                    render={props => (
                      <AllComments
                        resourceType={RESOURCE_TYPES.PRESSURE_MONITOR}
                        resourceSlug={pillarId}
                        isCommentLoading={isCommentLoading}
                        getAllComments={getAllComments}
                        deleteComment={deleteComment}
                        createComment={createComment}
                        editComment={editComment}
                        getAllTags={getAllTags}
                        tags={tags}
                        comments={comments}
                      />
                    )}
                  />
                </TabPane>
              )}
              <TabPane tab="Assignment History" key="assignment-history">
                <Route
                  path={`${match.url}/assignment-history`}
                  exact
                  render={props => (
                    <AssignmentHistory
                      assignmentHistory={assignmentHistory}
                      getAssignmentHistory={getAssignmentHistory}
                      resourceId={id}
                      isLoading={isHistoryLoading}
                    />
                  )}
                />
              </TabPane>
              <TabPane tab="Edit History" key="edit-history">
                <Route
                  path={`${match.url}/edit-history`}
                  exact
                  render={props => (
                    <EditHistory
                      editHistory={editHistory}
                      getEditHistory={getEditHistory}
                      resourceId={id}
                      isLoading={isHistoryLoading}
                    />
                  )}
                />
              </TabPane>
            </Tabs>
          </Fragment>
        </Switch>
      </section>
    )
  }
}

const mapStateToProps = ({ account }) => ({
  userRole: account.profile.role,
})

export default connect(mapStateToProps)(PressureMonitor)
